import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card } from '@totalsoft/rocket-ui';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { GENERIC_ENTITY_FLOW_LIST_QUERY } from '../queries';
import { getListBrowserStore } from '@ts-private/list-browser';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useMemo } from 'react';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import GenericEntityFlowItem from './GenericEntityFlowItem';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_GENERIC_ENTITY_FLOW } from '../mutations';
import { GENERIC_ENTITY_FLOW_DELETED } from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import useSubscription from 'hooks/apollo/useSubscription';

export const genericEntityFlowStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'flowCode',
      caption: 'GenericEntityFlow.Code',
      type: 'text'
    },
    {
      name: 'flowName',
      caption: 'GenericEntityFlow.Name',
      type: 'text'
    },
    {
      name: 'freeTxt',
      caption: 'GenericEntityFlow.Comments',
      type: 'text'
    },
    {
      name: 'enabledYN',
      caption: 'GenericEntityFlow.IsActive',
      type: 'boolean'
    }
  ],
  orderByFields: [
    {
      caption: 'GenericEntityFlow.Code',
      value: 'FlowCode'
    },
    {
      caption: 'GenericEntityFlow.Name',
      value: 'FlowName'
    }
  ],
  pager: {
    orderBy: 'FlowName',
    direction: 'asc'
  }
});

const GenericEntityFlowListBrowser = ({ entityContext }) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: GENERIC_ENTITY_FLOW_LIST_QUERY,
        name: 'genericEntityFlows',
        variables: { entityContext }
      }
    }),
    [entityContext]
  );

  // #region Delete

  useSubscription(GENERIC_ENTITY_FLOW_DELETED, {
    onData: ({ client, data }) => {
      const { deletedFlowId } = data.data.genericEntityFlowDeleted;
      client.cache.evict({ id: `GenericEntityFlow:${deletedFlowId}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericEntityFlows' });
      client.cache.gc();
      addToast(t('GenericEntityFlow.GenericEntityFlowDeleted'), 'success');
    }
  });

  const [deleteGenericEntityFlow] = useMutation(DELETE_GENERIC_ENTITY_FLOW);

  const handleDelete = useCallback(
    id => {
      confirm(
        () => {
          deleteGenericEntityFlow({ variables: { flowId: id } });
        },
        {
          content: t('GenericEntityFlow.DeleteGenericEntityFlow')
        }
      );
    },
    [deleteGenericEntityFlow, confirm, t]
  );

  // #endregion

  const itemComponent = useCallback(({ data }) => <GenericEntityFlowItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'code', caption: 'GenericEntityFlow.Code' },
      { name: 'name', caption: 'GenericEntityFlow.Name' },
      { name: 'description', caption: 'GenericEntityFlow.Description' },
      { name: 'isActive', caption: 'GenericEntityFlow.IsActive', width: 15, align: 'center' },
      { name: 'principalFlow', caption: 'GenericEntityFlow.PrincipalFlow', width: 15, align: 'center' },
      { name: 'actions', caption: 'Actions' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='generic-entity-flow-browser' store={genericEntityFlowStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={genericEntityFlowStore} options={fetchingOptions} />
      <ApolloFetchingComponent store={genericEntityFlowStore} options={fetchingOptions} />
    </Card>
  );
};

GenericEntityFlowListBrowser.propTypes = {
  entityContext: PropTypes.string
};
export default GenericEntityFlowListBrowser;
